import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Loader2,
  DollarSign,
  Package,
  Tag,
  Image as ImageIcon,
  Car,
  Search,
  Warehouse as WarehouseIcon,
  ArrowRightLeft,
  X,
} from 'lucide-react'
import { apiGet, apiDelete, apiPut, formatMXN } from '../lib/api'
import { productImageUrl } from '../lib/image-url'

interface ProductoDetalle {
  id: string
  sku: string
  slug: string
  nombre: string
  descripcion: string
  categoriaId: string
  categoriaNombre: string
  brandId: string
  brandNombre: string
  precio: number
  precioOferta: number | null
  costo: number
  stock: number
  pesoKg: number
  imagen: string
  imagen2?: string
  imagen3?: string
  imagen4?: string
  imagen5?: string
  imagenBaja?: string
  destacado: boolean
  enOferta: boolean
  activo: boolean
  metaTitulo: string
  metaDescripcion: string
  creadoEn: string
  actualizadoEn: string
  compatibilidades: Array<{
    id: string
    vehicleBrandId: string
    vehicleBrandNombre: string
    vehicleModelId: string
    vehicleModelNombre: string
    yearFrom: number | null
    yearTo: number | null
  }>
}

export function ProductoDetalle() {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { data, isLoading, error } = useQuery<{ data: ProductoDetalle }>({
    queryKey: ['producto', id],
    queryFn: () => apiGet(`/api/admin/productos/${id}`),
  })

  const deleteMut = useMutation({
    mutationFn: () => apiDelete(`/api/admin/productos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productos'] })
      navigate({ to: '/productos' })
    },
  })

  if (isLoading) {
    return (
      <>
        <Topbar title="Producto" subtitle="Cargando…" />
        <div className="grid place-items-center py-20 text-ink-500">
          <Loader2 className="size-8 animate-spin" />
        </div>
      </>
    )
  }

  if (error || !data) {
    return (
      <>
        <Topbar title="Producto" subtitle="Error" />
        <div className="p-6">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            No se pudo cargar el producto.
          </div>
        </div>
      </>
    )
  }

  const p = data.data

  return (
    <>
      <Topbar
        title={p.nombre}
        subtitle={`SKU: ${p.sku}`}
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate({ to: '/productos' })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <ArrowLeft className="size-3.5" /> Volver
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: '/productos/$id/editar', params: { id } })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Edit className="size-3.5" /> Editar
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
            >
              <Trash2 className="size-3.5" /> Eliminar
            </button>
          </>
        }
      />
      <div className="space-y-4 p-6">
        <div className="flex gap-2">
          {p.activo ? (
            <Badge variant="success">Activo</Badge>
          ) : (
            <Badge variant="danger">Inactivo</Badge>
          )}
          {p.destacado && <Badge variant="info">Destacado</Badge>}
          {p.enOferta && <Badge variant="warning">En oferta</Badge>}
          {p.stock === 0 && <Badge variant="danger">Agotado</Badge>}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {/* Info general */}
          <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Package className="size-4 text-brand-600" /> Información general
            </div>
            <dl className="grid gap-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">Nombre</dt>
                <dd className="mt-0.5 font-medium text-ink-900">{p.nombre}</dd>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-500">SKU</dt>
                  <dd className="mt-0.5 font-mono text-ink-900">{p.sku}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-500">Slug</dt>
                  <dd className="mt-0.5 font-mono text-xs text-ink-700">{p.slug}</dd>
                </div>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">Descripción</dt>
                <dd className="prose prose-sm mt-0.5 max-w-none text-ink-700">
                  {p.descripcion ? (
                    <div dangerouslySetInnerHTML={{ __html: p.descripcion }} />
                  ) : (
                    <span className="text-ink-400">Sin descripción</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Galería */}
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
              <ImageIcon className="size-4 text-brand-600" /> Galería
            </div>
            {p.imagen ? (
              <img
                src={productImageUrl(p.imagen)}
                alt={p.nombre}
                referrerPolicy="no-referrer"
                onError={(e) => {
                  ;(e.currentTarget as HTMLImageElement).src = '/img/sin-imagen.jpg'
                }}
                className="w-full rounded-lg border border-ink-200 object-contain"
              />
            ) : (
              <div className="grid h-48 place-items-center rounded-lg border-2 border-dashed border-ink-200 text-sm text-ink-400">
                Sin imagen
              </div>
            )}
            {p.imagen && (
              <div className="mt-2 truncate font-mono text-[10px] text-ink-500">{p.imagen}</div>
            )}
            {(p.imagen2 || p.imagen3 || p.imagen4 || p.imagen5) && (
              <div className="mt-3 grid grid-cols-4 gap-2">
                {[p.imagen2, p.imagen3, p.imagen4, p.imagen5].filter(Boolean).map((url, i) => (
                  <img
                    key={i}
                    src={productImageUrl(url!)}
                    alt={`Imagen ${i + 2}`}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      ;(e.currentTarget as HTMLImageElement).src = '/img/sin-imagen.jpg'
                    }}
                    className="aspect-square rounded-lg border border-ink-200 object-contain"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Precios y stock */}
          <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
              <DollarSign className="size-4 text-brand-600" /> Precios y stock
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Precio</div>
                <div className="mt-1 text-xl font-extrabold text-ink-900">{formatMXN(p.precio)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Oferta</div>
                <div className="mt-1 text-xl font-extrabold text-ink-900">
                  {p.precioOferta ? formatMXN(p.precioOferta) : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Stock</div>
                <div className="mt-1 text-xl font-extrabold text-ink-900">{p.stock}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Peso (kg)</div>
                <div className="mt-1 text-xl font-extrabold text-ink-900">{p.pesoKg}</div>
              </div>
            </div>
          </div>

          {/* Categoría / Marca */}
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Tag className="size-4 text-brand-600" /> Clasificación
            </div>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">Categoría</dt>
                <dd className="mt-0.5 font-medium text-ink-900">
                  {p.categoriaNombre || <span className="text-ink-400">Sin categoría</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">Marca</dt>
                <dd className="mt-0.5 font-medium text-ink-900">
                  {p.brandNombre || <span className="text-ink-400">Sin marca</span>}
                </dd>
              </div>
            </dl>
          </div>

          {/* Compatibilidades */}
          <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-3">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Car className="size-4 text-brand-600" /> Compatibilidades de vehículo
              <span className="text-xs font-normal text-ink-500">
                ({p.compatibilidades.length})
              </span>
            </div>
            {p.compatibilidades.length === 0 ? (
              <div className="rounded-lg border border-dashed border-ink-200 p-4 text-center text-sm text-ink-400">
                Este producto no tiene compatibilidades registradas
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Marca</th>
                      <th className="py-2 pr-4 font-medium">Modelo</th>
                      <th className="py-2 pr-4 font-medium">Años</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.compatibilidades.map((c) => (
                      <tr key={c.id} className="border-t border-ink-100">
                        <td className="py-2 pr-4 font-medium text-ink-900">
                          {c.vehicleBrandNombre || '—'}
                        </td>
                        <td className="py-2 pr-4 text-ink-700">{c.vehicleModelNombre || '—'}</td>
                        <td className="py-2 pr-4 text-ink-700">
                          {c.yearFrom || c.yearTo
                            ? `${c.yearFrom ?? '?'} – ${c.yearTo ?? '?'}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Stock por bodega */}
          <div className="lg:col-span-3">
            <StockBodegasCard productId={id} />
          </div>

          {/* SEO */}
          <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-3">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Search className="size-4 text-brand-600" /> SEO
            </div>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">Meta título</dt>
                <dd className="mt-0.5 text-ink-900">
                  {p.metaTitulo || <span className="text-ink-400">Sin meta título</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">Meta descripción</dt>
                <dd className="mt-0.5 text-ink-700">
                  {p.metaDescripcion || (
                    <span className="text-ink-400">Sin meta descripción</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {confirmDelete && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-ink-900">¿Eliminar producto?</h3>
              <p className="mt-2 text-sm text-ink-700">
                El producto <strong>{p.nombre}</strong> se marcará como inactivo y no aparecerá en
                el catálogo. Esta acción se puede revertir editando el producto.
              </p>
              {deleteMut.isError && (
                <div className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">
                  {(deleteMut.error as Error).message}
                </div>
              )}
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => deleteMut.mutate()}
                  disabled={deleteMut.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMut.isPending && <Loader2 className="size-3.5 animate-spin" />}
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ProductoDetalle

// ----------------------------------------------------------------------
// Card "Stock por bodega" con modal de transferencia
// ----------------------------------------------------------------------

interface StockRow {
  warehouseId: string
  code: string
  nombre: string
  esDefault: boolean
  stock: number
  reservado: number
  disponible: number
}

interface StockData {
  producto: { id: string; sku: string; nombre: string; stockLegacy: number }
  data: StockRow[]
  totales: { stock: number; reservado: number; disponible: number }
}

function StockBodegasCard({ productId }: { productId: string }) {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [fromWh, setFromWh] = useState('')
  const [toWh, setToWh] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery<StockData>({
    queryKey: ['stock-producto', productId],
    queryFn: () => apiGet(`/api/admin/stock/producto/${productId}`),
  })

  const transferMutation = useMutation({
    mutationFn: (payload: any) => apiPut('/api/admin/stock/transfer', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-producto', productId] })
      closeModal()
    },
    onError: (e: Error) => setError(e.message),
  })

  function openModal() {
    setFromWh('')
    setToWh('')
    setQuantity('1')
    setError(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setError(null)
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const qty = parseInt(quantity)
    if (!fromWh || !toWh) {
      setError('Selecciona bodega origen y destino')
      return
    }
    if (fromWh === toWh) {
      setError('Las bodegas deben ser distintas')
      return
    }
    if (!qty || qty < 1) {
      setError('Cantidad inválida')
      return
    }
    transferMutation.mutate({
      productId,
      fromWarehouseId: fromWh,
      toWarehouseId: toWh,
      quantity: qty,
    })
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <WarehouseIcon className="size-4 text-brand-600" /> Stock por bodega
          {data && (
            <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-700">
              {data.data.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={openModal}
          disabled={!data || data.data.length < 2}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <ArrowRightLeft className="size-3.5" /> Transferir
        </button>
      </div>

      {isLoading && (
        <div className="grid place-items-center py-6 text-ink-500">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}

      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                <th className="py-2 pr-3 font-medium">Bodega</th>
                <th className="py-2 pr-3 text-right font-medium">Stock</th>
                <th className="py-2 pr-3 text-right font-medium">Reservado</th>
                <th className="py-2 pr-3 text-right font-medium">Disponible</th>
              </tr>
            </thead>
            <tbody>
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-ink-400">
                    No hay bodegas configuradas
                  </td>
                </tr>
              )}
              {data.data.map((r) => (
                <tr key={r.warehouseId} className="border-t border-ink-100">
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink-900">{r.nombre}</span>
                      <span className="font-mono text-[10px] text-ink-500">{r.code}</span>
                      {r.esDefault && (
                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                          default
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right text-ink-900">{r.stock}</td>
                  <td className="py-2 pr-3 text-right text-ink-700">{r.reservado}</td>
                  <td className="py-2 pr-3 text-right font-semibold text-ink-900">
                    {r.disponible}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-ink-200 bg-ink-50 font-semibold">
                <td className="py-2 pr-3 text-ink-700">Total</td>
                <td className="py-2 pr-3 text-right text-ink-900">{data.totales.stock}</td>
                <td className="py-2 pr-3 text-right text-ink-700">{data.totales.reservado}</td>
                <td className="py-2 pr-3 text-right text-ink-900">{data.totales.disponible}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && data && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
          <form onSubmit={onSubmit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink-900">Transferir stock</h3>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1 text-ink-500 hover:bg-ink-100"
              >
                <X className="size-4" />
              </button>
            </div>
            {error && (
              <div className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">{error}</div>
            )}
            <div className="grid gap-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ink-700">Desde bodega</span>
                <select
                  value={fromWh}
                  onChange={(e) => setFromWh(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  required
                >
                  <option value="">Seleccionar…</option>
                  {data.data.map((r) => (
                    <option key={r.warehouseId} value={r.warehouseId}>
                      {r.nombre} (disp: {r.disponible})
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ink-700">Hacia bodega</span>
                <select
                  value={toWh}
                  onChange={(e) => setToWh(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  required
                >
                  <option value="">Seleccionar…</option>
                  {data.data
                    .filter((r) => r.warehouseId !== fromWh)
                    .map((r) => (
                      <option key={r.warehouseId} value={r.warehouseId}>
                        {r.nombre}
                      </option>
                    ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ink-700">Cantidad</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  required
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={transferMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {transferMutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Transferir
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
